/**
 * The bidding box: levels down, denominations across (♣ ♦ ♥ ♠ NT), with
 * double and redouble beside it. Generated from `legalCalls` like every other
 * bid panel; a bid below the standing one is dead print. Four levels show at a
 * time from the lowest one still open, because seven rows do not fit a phone.
 */
import { useRef, useState } from 'react'
import type { BidLayoutProps, LegalCall } from '../types'
import { Strainy } from '../../table/atoms'

const STRAINS = ['C', 'D', 'H', 'S', 'NT'] as const
const LEVELS = [1, 2, 3, 4, 5, 6, 7] as const
const ROWS = 4

const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

export function BridgeBids({ calls, selected, onSelect }: BidLayoutProps): JSX.Element {
  const [page, setPage] = useState(0)
  const live = new Map<string, LegalCall>()
  for (const call of calls) live.set(call.key, call)
  // While somebody else calls, nothing is open, but the box keeps the rows it
  // last showed rather than collapsing to its header and pushing the table
  // about; the first time round that is the bottom four levels.
  const lastOpen = useRef<readonly number[]>(LEVELS.slice(0, ROWS))
  const liveLevels = LEVELS.filter((l) => STRAINS.some((s) => live.has(`${l}${s}`)))
  if (liveLevels.length > 0) lastOpen.current = liveLevels
  const open = liveLevels.length > 0 ? liveLevels : lastOpen.current
  const pages = Math.max(1, Math.ceil(open.length / ROWS))
  const shown = Math.min(page, pages - 1)
  // Always four rows, however few levels are still open: near the top of the
  // auction the page is padded with the levels just below it (dead print), so
  // the box is one height from the first call to the last.
  const from = Math.max(0, Math.min(shown * ROWS, open.length - ROWS))
  const page0 = open.slice(from, from + ROWS)
  const rows =
    page0.length >= ROWS
      ? page0
      : [...LEVELS.filter((l) => l < (page0[0] ?? 8)).slice(-(ROWS - page0.length)), ...page0]

  const cell = (key: string, label: React.ReactNode) => {
    const call = live.get(key)
    const sel = selected?.key === key
    return (
      <button
        key={key}
        type="button"
        className={`cell${call ? '' : ' dead'}${sel ? ' sel' : ''}`}
        disabled={!call}
        onClick={() => onSelect(sel ? null : (call ?? null))}
        aria-pressed={sel}
      >
        {label}
      </button>
    )
  }
  const doubles = calls.filter((c) => isRecord(c.call) && (c.call.type === 'double' || c.call.type === 'redouble'))

  return (
    <div>
      <div className="ladder">
        <div className="hd" />
        {STRAINS.map((s) => (
          <div key={s} className={s === 'NT' ? 'hd nt' : 'hd'}>
            <Strainy strain={s} />
          </div>
        ))}
        {rows.map((level) => (
          <Row key={level} level={level}>
            {STRAINS.map((s) => cell(`${level}${s}`, level))}
          </Row>
        ))}
      </div>
      <div className="ladder-misere">
        {pages > 1 ? (
          <button type="button" className="cell" onClick={() => setPage((shown + 1) % pages)}>
            {shown + 1 < pages ? `Higher: ${open[(shown + 1) * ROWS]} and up` : `Back to ${open[0]}`}
          </button>
        ) : <div className="cell dead" aria-hidden="true" />}
        {doubles.length > 0
          ? cell(doubles[0].key, doubles[0].call && isRecord(doubles[0].call) && doubles[0].call.type === 'redouble' ? 'Redouble' : 'Double')
          : <div className="cell dead">Double</div>}
      </div>
    </div>
  )
}

function Row({ level, children }: { level: number; children: React.ReactNode }): JSX.Element {
  return (
    <>
      <div className="lv">{level}</div>
      {children}
    </>
  )
}
